import type { Account, BankConnection, BankConnectionAccount } from '@fd/schemas';
import { AlertTriangle, Check, ChevronDown, ChevronRight, Clock, ExternalLink, Landmark, ListChecks, RefreshCw, Search, Unlink } from 'lucide-react';
import { useEffect, useState } from 'react';
import { bankingApi, bankReturnUrl, LUNCHFLOW_URL, type AccountChoice, type Institution } from '../app/banking.ts';
import { serverErrorText } from '../app/server.ts';
import { useAccounts, useEntities, useHousehold, usePersons, type Synced } from '../app/data.ts';
import { useT } from '../app/i18n.tsx';
import { hrefFor } from '../app/router.tsx';
import { useSession } from '../app/session.tsx';
import { Avatar, Card, Modal, Money, Segmented, useToast } from '../ui/primitives.tsx';

// Open Banking in the app (BANKING.md §2): choose a bank → sign in there → back here to choose
// the accounts and their owners. Everything after that (downloads, pending → booked, transfers
// between own accounts) happens on the server and arrives through sync like any other change.

type T = ReturnType<typeof useT>;

const DAY_MS = 86_400_000;

const errorText = (t: T, error: unknown) => serverErrorText(t, error, 'banks.error');

export function useBankConnections(): Synced<BankConnection>[] {
  const { data } = useEntities<BankConnection>('bankConnection');
  return (data ?? []).filter((c) => c.deletedAt === null && c.status !== 'DISCONNECTED').sort((a, b) => a.institutionName.localeCompare(b.institutionName));
}

const mark = (name: string) => name.trim().charAt(0).toUpperCase();

// ------------------------------------------------------------------------------ choose a bank

export function BankConnectModal({ open, personId, onClose }: { open: boolean; personId: string | null; onClose(): void }) {
  const t = useT();
  const { householdId } = useSession();
  const household = useHousehold();
  const [country, setCountry] = useState<'GB' | 'PL'>('GB');
  const [query, setQuery] = useState('');
  const [banks, setBanks] = useState<Institution[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setCountry(household?.baseCurrency === 'PLN' ? 'PL' : 'GB');
    setQuery('');
    setBusy(null);
    setError(null);
  }, [open, household?.baseCurrency]);

  useEffect(() => {
    if (!open || !householdId) return;
    let live = true;
    setBanks(null);
    bankingApi
      .institutions(householdId, country)
      .then((list) => live && setBanks(list))
      .catch((cause: unknown) => {
        if (!live) return;
        setBanks([]);
        setError(errorText(t, cause));
      });
    return () => {
      live = false;
    };
  }, [open, householdId, country, t]);

  async function choose(bank: Institution) {
    if (!householdId) return;
    setBusy(bank.id);
    setError(null);
    try {
      const url = await bankingApi.connect(householdId, { provider: bank.provider, institutionId: bank.id, country, returnUrl: bankReturnUrl(personId), connectionId: null });
      location.assign(url);
    } catch (cause) {
      setBusy(null);
      setError(errorText(t, cause));
    }
  }

  const needle = query.trim().toLocaleLowerCase(t.locale);
  const shown = (banks ?? []).filter((b) => !needle || b.name.toLocaleLowerCase(t.locale).includes(needle));

  return (
    <Modal
      open={open}
      title={t.t('banks.connectTitle')}
      onClose={onClose}
      width={520}
      footer={
        <button className="btn btn--ghost" type="button" onClick={onClose}>
          {t.t('common.cancel')}
        </button>
      }
    >
      <div className="stack gap-4">
        <p className="text-secondary" style={{ margin: 0, fontSize: 13 }}>
          {t.t('banks.connectHint')}
        </p>
        <Segmented<'GB' | 'PL'>
          label={t.t('banks.country')}
          value={country}
          onChange={setCountry}
          options={[
            { value: 'GB', label: t.t('banks.countryGB') },
            { value: 'PL', label: t.t('banks.countryPL') },
          ]}
        />
        <label className="filters__search bank-search">
          <Search aria-hidden="true" />
          <input className="input" type="search" placeholder={t.t('banks.search')} aria-label={t.t('banks.search')} value={query} onChange={(e) => setQuery(e.target.value)} />
        </label>
        <div className="bank-list" aria-busy={banks === null}>
          {banks === null && <div className="bank-list__empty">…</div>}
          {banks !== null && shown.length === 0 && !error && <div className="bank-list__empty">{t.t('banks.noBanks')}</div>}
          {shown.map((bank) => (
            <button key={bank.id} type="button" className="bank-option" disabled={busy !== null} onClick={() => void choose(bank)}>
              <span className="account-mark bank-option__mark" aria-hidden="true">
                {mark(bank.name)}
              </span>
              <span className="grow">{busy === bank.id ? t.t('banks.opening', { bank: bank.name }) : bank.name}</span>
              <ChevronRight aria-hidden="true" size={16} />
            </button>
          ))}
        </div>
        {(banks ?? []).some((b) => b.provider === 'FAKE_BANK') && <p className="list-row__meta" style={{ margin: 0 }}>{t.t('banks.testBanks')}</p>}
        {(banks ?? []).some((b) => b.provider === 'LUNCHFLOW') && <p className="list-row__meta" style={{ margin: 0 }}>{t.t('banks.lunchFlow.hint')}</p>}
        {/* Trading 212 is not a bank here: it has its own read-only API, connected with a key. */}
        <p className="list-row__meta" style={{ margin: 0 }}>
          {t.t('banks.investmentsElsewhere')}{' '}
          <a className="link-button" href={hrefFor({ page: 'savings' })} onClick={onClose}>
            {t.t('banks.investmentsLink')}
          </a>
        </p>
        {error && (
          <p className="field__error" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}

// ------------------------------------------------------------------------------ choose accounts

type Draft = { include: boolean; target: string; owners: string[]; name: string; type: BankConnectionAccount['type'] };

const TYPE_OF: Record<BankConnectionAccount['type'], Account['type']> = { CURRENT: 'CURRENT', SAVINGS: 'SAVINGS', CREDIT_CARD: 'CREDIT_CARD' };

/** Accounts kept by hand that this bank account could continue. */
/** The last four digits at the end of a name ("Classic 8168", "Lloyds ••8168"), if any. */
const lastDigits = (name: string) => /(\d{4})\D*$/.exec(name)?.[1] ?? null;

function linkable(accounts: readonly Account[], entry: BankConnectionAccount): Account[] {
  return accounts.filter((a) => a.provider === 'MANUAL' && !a.bankConnectionId && !a.archivedAt && a.currency === entry.currency && a.type === TYPE_OF[entry.type]);
}

export function BankAccountsModal({ connection, personId, onClose }: { connection: Synced<BankConnection> | null; personId: string | null; onClose(): void }) {
  const t = useT();
  const { householdId, requestSync } = useSession();
  const toast = useToast();
  const household = useHousehold();
  const persons = usePersons();
  const accounts = useAccounts();
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pending = connection?.accounts.filter((a) => a.status === 'PENDING') ?? [];
  const connectionId = connection?.id ?? null;
  // Bank accounts already followed through another connection (a second consent at the same bank).
  const connections = useBankConnections();
  const followed = new Map<string, string>();
  for (const other of connections) {
    if (other.id === connectionId) continue;
    for (const entry of other.accounts) if (entry.status === 'LINKED' && entry.accountId) followed.set(entry.externalId, entry.accountId);
  }
  const followedName = (externalId: string) => accounts.find((a) => a.id === followed.get(externalId))?.name ?? '—';

  useEffect(() => {
    if (!connection) return;
    const defaultOwners = personId ? [personId] : household?.ownerPersonId ? [household.ownerPersonId] : [];
    const next: Record<string, Draft> = {};
    const taken = new Set<string>();
    for (const entry of connection.accounts.filter((a) => a.status === 'PENDING')) {
      // A hand-kept account at the same bank, of the same kind, is most likely this one — the one
      // with the same last digits ("Classic 8168" and "Lloyds ••8168"), never one with other digits,
      // and each hand-kept account for one bank account only.
      const bankWord = connection.institutionName.split(' ')[0]!.toLocaleLowerCase();
      const tail = entry.mask?.replace(/\D/g, '').slice(-4) || lastDigits(entry.name);
      const candidates = linkable(accounts, entry).filter((a) => !taken.has(a.id));
      const guess =
        candidates.find((a) => tail !== null && lastDigits(a.name) === tail) ??
        candidates.find((a) => lastDigits(a.name) === null && `${a.name} ${a.institutionName ?? ''}`.toLocaleLowerCase().includes(bankWord));
      if (guess) taken.add(guess.id);
      next[entry.externalId] = { include: !followed.has(entry.externalId), target: guess?.id ?? 'NEW', owners: guess ? [...guess.owners] : defaultOwners, name: entry.name, type: entry.type };
    }
    setDrafts(next);
    setBusy(false);
    setError(null);
    // Only when a different connection is opened; later account changes must not reset the form.
  }, [connectionId]);

  const update = (externalId: string, patch: Partial<Draft>) => setDrafts((all) => ({ ...all, [externalId]: { ...all[externalId]!, ...patch } }));

  async function save() {
    if (!householdId || !connection) return;
    const choices: AccountChoice[] = pending.map((entry) => {
      const draft = drafts[entry.externalId];
      if (!draft?.include) return { externalId: entry.externalId, action: 'SKIP', accountId: null, owners: [], name: null };
      return draft.target === 'NEW'
        ? { externalId: entry.externalId, action: 'ADD', accountId: null, owners: draft.owners, name: draft.name.trim() || null, type: draft.type }
        : { externalId: entry.externalId, action: 'LINK', accountId: draft.target, owners: draft.owners, name: null };
    });
    setBusy(true);
    setError(null);
    try {
      await bankingApi.chooseAccounts(householdId, connection.id, choices);
      requestSync();
      const added = choices.filter((c) => c.action !== 'SKIP').length;
      toast(added > 0 ? t.plural('banks.added', added) : t.t('banks.nothingChosen'), <Check aria-hidden="true" />);
      onClose();
    } catch (cause) {
      setBusy(false);
      setError(errorText(t, cause));
    }
  }

  return (
    <Modal
      open={connection !== null && pending.length > 0}
      title={connection ? t.t('banks.chooseTitle', { bank: connection.institutionName }) : ''}
      onClose={busy ? () => undefined : onClose}
      width={620}
      footer={
        <>
          <button className="btn btn--ghost" type="button" disabled={busy} onClick={onClose}>
            {t.t('common.cancel')}
          </button>
          <button className="btn btn--primary" type="button" disabled={busy} onClick={() => void save()}>
            {busy ? <RefreshCw aria-hidden="true" className="spin" /> : <Check aria-hidden="true" />}
            {busy && connection ? t.t('banks.importing', { bank: connection.institutionName }) : t.t('banks.save')}
          </button>
        </>
      }
    >
      <div className="stack gap-4">
        <p className="text-secondary" style={{ margin: 0, fontSize: 13 }}>
          {connection?.provider === 'LUNCHFLOW' ? `${t.t('banks.lunchFlow.hint')} ${t.t('banks.chooseHint')}` : t.t('banks.chooseHint')}
        </p>
        {pending.map((entry) => {
          const draft = drafts[entry.externalId];
          if (!draft) return null;
          const candidates = linkable(accounts, entry);
          return (
            <section key={entry.externalId} className={`bank-choice${draft.include ? '' : ' bank-choice--off'}`}>
              <label className="bank-choice__head">
                <input
                  type="checkbox"
                  checked={draft.include}
                  disabled={followed.has(entry.externalId)}
                  aria-label={`${t.t('banks.include')}: ${entry.name}`}
                  onChange={(e) => update(entry.externalId, { include: e.target.checked })}
                />
                <span className="stack grow" style={{ gap: 2, minWidth: 0 }}>
                  <strong className="truncate">{entry.name}</strong>
                  <span className="list-row__meta">{[entry.institutionName, t.t(`accounts.typeValue.${draft.type}`), entry.mask, entry.currency].filter(Boolean).join(' · ')}</span>
                  {followed.has(entry.externalId) && <span className="list-row__meta">{t.t('banks.alreadyFollowed', { name: followedName(entry.externalId) })}</span>}
                </span>
                <strong className="num">
                  <Money minor={entry.balanceMinor} currency={entry.currency} />
                </strong>
              </label>
              {draft.include && (
                <div className="stack gap-3 bank-choice__body">
                  <div className="form-grid">
                    <label className="field">
                      <span className="field__label">{t.t('banks.becomes')}</span>
                      <select className="select" value={draft.target} onChange={(e) => update(entry.externalId, { target: e.target.value, owners: e.target.value === 'NEW' ? draft.owners : [...(candidates.find((a) => a.id === e.target.value)?.owners ?? draft.owners)] })}>
                        <option value="NEW">{t.t('banks.asNew')}</option>
                        {candidates.map((a) => (
                          <option key={a.id} value={a.id}>
                            {t.t('banks.linkTo', { name: a.name })}
                          </option>
                        ))}
                      </select>
                    </label>
                    {draft.target === 'NEW' ? (
                      <>
                        <label className="field">
                          <span className="field__label">{t.t('accounts.name')}</span>
                          <input className="input" value={draft.name} maxLength={120} onChange={(e) => update(entry.externalId, { name: e.target.value })} />
                        </label>
                        {/* Some providers (Lunch Flow) do not say what the account is; it cannot be changed later. */}
                        <label className="field">
                          <span className="field__label">{t.t('accounts.type')}</span>
                          <select className="select" value={draft.type} onChange={(e) => update(entry.externalId, { type: e.target.value as Draft['type'] })}>
                            {(['CURRENT', 'SAVINGS', 'CREDIT_CARD'] as const).map((value) => (
                              <option key={value} value={value}>
                                {t.t(`accounts.typeValue.${value}`)}
                              </option>
                            ))}
                          </select>
                        </label>
                        <p className="list-row__meta" style={{ margin: 0, alignSelf: 'end' }}>
                          {t.t('banks.typeHint')}
                        </p>
                      </>
                    ) : (
                      <p className="list-row__meta" style={{ margin: 0, alignSelf: 'end' }}>
                        {t.t('banks.linkHint')}
                      </p>
                    )}
                  </div>
                  {persons.length > 0 && (
                    <div className="stack gap-2">
                      <span className="field__label">{t.t('accounts.owners')}</span>
                      <div className="chip-group">
                        {persons.map((p) => (
                          <button
                            key={p.id}
                            type="button"
                            className="chip"
                            aria-pressed={draft.owners.includes(p.id)}
                            onClick={() => update(entry.externalId, { owners: draft.owners.includes(p.id) ? draft.owners.filter((o) => o !== p.id) : [...draft.owners, p.id] })}
                          >
                            <Avatar name={p.displayName} color={p.color} />
                            {p.displayName}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </section>
          );
        })}
        {error && (
          <p className="field__error" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}

// ------------------------------------------------------------------------------ connections

const TONE: Record<BankConnection['status'], 'ok' | 'warn' | 'bad'> = {
  CONNECTED: 'ok',
  SYNCING: 'ok',
  CONSENT_EXPIRING: 'warn',
  ACTION_REQUIRED: 'warn',
  RATE_LIMITED: 'warn',
  CONSENT_EXPIRED: 'bad',
  EXPIRED: 'bad',
  ERROR: 'bad',
  DISCONNECTED: 'bad',
  UNSUPPORTED: 'bad',
};

/** What needs the owner: accounts to decide about, access ending or ended, a failed download. */
export function BankBanners({ onChoose }: { onChoose(connection: Synced<BankConnection>): void }) {
  const t = useT();
  const connections = useBankConnections();
  const reconnect = useReconnect();
  const renewed = useRenewed();
  return (
    <>
      {connections.map((c) => {
        const pending = c.accounts.some((a) => a.status === 'PENDING');
        const bank = c.institutionName;
        // Lunch Flow: the banks are renewed in Lunch Flow itself, and a passing failure is not alarming.
        if (c.provider === 'LUNCHFLOW') {
          if (c.lastErrorCode === 'UNAUTHORIZED' || c.lastErrorCode === 'FORBIDDEN') {
            return (
              <div key={c.id} className="banner banner--warning">
                <AlertTriangle size={16} aria-hidden="true" />
                <span className="grow">
                  <strong>{bank}</strong> · {t.dynamic(`banks.error.BANK_${c.lastErrorCode}`)}
                </span>
              </div>
            );
          }
          if (c.reauthorizationRequired || c.status === 'CONSENT_EXPIRED' || c.status === 'ACTION_REQUIRED') {
            const renew = c.accounts.filter((a) => a.status === 'LINKED' && a.visible !== false && a.access === 'RECONNECT');
            const banks = [...new Set((renew.length > 0 ? renew : c.accounts.filter((a) => a.status === 'LINKED')).map((a) => a.institutionName ?? bank))].join(', ') || bank;
            return (
              <div key={c.id} className="banner banner--warning">
                <AlertTriangle size={16} aria-hidden="true" />
                <span className="grow">{t.t('banks.lunchFlow.reauthBanner', { bank: banks })}</span>
                <a className="btn btn--secondary btn--sm" href={LUNCHFLOW_URL} target="_blank" rel="noopener noreferrer">
                  <ExternalLink aria-hidden="true" />
                  {t.t('banks.lunchFlow.openLunchFlow')}
                </a>
              </div>
            );
          }
          // A week before the bank's 90 days in Lunch Flow run out (reckoned: Lunch Flow does not say).
          if (c.status === 'CONSENT_EXPIRING' && c.consentExpiresAt) {
            const banks = [...new Set(c.accounts.filter((a) => a.status === 'LINKED' && a.visible !== false).map((a) => a.institutionName ?? bank))].join(', ') || bank;
            return (
              <div key={c.id} className="banner banner--warning">
                <AlertTriangle size={16} aria-hidden="true" />
                <span className="grow">{t.t('banks.lunchFlow.expiringBanner', { bank: banks, date: t.date(c.consentExpiresAt.slice(0, 10), 'dayMonth') })}</span>
                <a className="btn btn--secondary btn--sm" href={LUNCHFLOW_URL} target="_blank" rel="noopener noreferrer">
                  <ExternalLink aria-hidden="true" />
                  {t.t('banks.lunchFlow.openLunchFlow')}
                </a>
                <button className="btn btn--ghost btn--sm" type="button" onClick={() => void renewed(c)}>
                  <Check aria-hidden="true" />
                  {t.t('banks.lunchFlow.renewed')}
                </button>
              </div>
            );
          }
          if (c.status === 'ERROR' || c.status === 'RATE_LIMITED') {
            return (
              <div key={c.id} className="banner banner--info">
                <Clock size={16} aria-hidden="true" />
                <span className="grow">{t.t('banks.lunchFlow.unavailableBanner')}</span>
              </div>
            );
          }
        }
        // Access to confirm again: the one case that needs the owner — history is not lost.
        if (c.reauthorizationRequired || c.status === 'CONSENT_EXPIRED' || c.status === 'EXPIRED' || c.status === 'ACTION_REQUIRED') {
          return (
            <div key={c.id} className="banner banner--warning">
              <AlertTriangle size={16} aria-hidden="true" />
              <span className="grow">
                <strong>{bank}</strong> · {t.t('banks.reauthBanner')}
              </span>
              <button className="btn btn--secondary btn--sm" type="button" onClick={() => void reconnect(c)}>
                {t.t('banks.reconnect')}
              </button>
            </div>
          );
        }
        if (c.status === 'CONSENT_EXPIRING') {
          const text = c.consentExpiresAt ? t.t('banks.expiringBanner', { bank, date: t.date(c.consentExpiresAt.slice(0, 10), 'dayMonth') }) : t.t('banks.expiredBanner', { bank });
          return (
            <div key={c.id} className="banner banner--warning">
              <AlertTriangle size={16} aria-hidden="true" />
              <span className="grow">{text}</span>
              <button className="btn btn--secondary btn--sm" type="button" onClick={() => void reconnect(c)}>
                {t.t('banks.reconnect')}
              </button>
            </div>
          );
        }
        if (c.status === 'ERROR') {
          return (
            <div key={c.id} className="banner banner--warning">
              <AlertTriangle size={16} aria-hidden="true" />
              <span className="grow">{t.t('banks.errorBanner', { bank })}</span>
            </div>
          );
        }
        if (pending) {
          return (
            <div key={c.id} className="banner banner--info">
              <ListChecks size={16} aria-hidden="true" />
              <span className="grow">{t.t('banks.pendingBanner', { bank })}</span>
              <button className="btn btn--secondary btn--sm" type="button" onClick={() => onChoose(c)}>
                {t.t('banks.chooseAccounts')}
              </button>
            </div>
          );
        }
        return null;
      })}
    </>
  );
}

/** The owner renewed the bank in Lunch Flow before its 90 days ran out: they count again from today. */
function useRenewed() {
  const t = useT();
  const { householdId, requestSync } = useSession();
  const toast = useToast();
  return async (connection: BankConnection) => {
    if (!householdId) return;
    try {
      const { consentExpiresAt } = await bankingApi.renewed(householdId, connection.id);
      requestSync();
      toast(consentExpiresAt ? t.t('banks.lunchFlow.renewedDone', { date: t.date(consentExpiresAt.slice(0, 10), 'dayMonth') }) : t.t('banks.lunchFlow.renewed'), <Check aria-hidden="true" />);
    } catch (cause) {
      toast(errorText(t, cause), <AlertTriangle aria-hidden="true" />);
    }
  };
}

/** Renewing consent: the same trip to the bank, for an existing connection. */
function useReconnect() {
  const t = useT();
  const { householdId } = useSession();
  const toast = useToast();
  return async (connection: BankConnection) => {
    if (!householdId) return;
    try {
      location.assign(
        await bankingApi.connect(householdId, {
          provider: connection.provider as Institution['provider'],
          institutionId: connection.institutionId,
          country: connection.country,
          returnUrl: bankReturnUrl(null),
          connectionId: connection.id,
        }),
      );
    } catch (cause) {
      toast(errorText(t, cause), <AlertTriangle aria-hidden="true" />);
    }
  };
}

export function BankConnectionsCard({ onChoose }: { onChoose(connection: Synced<BankConnection>): void }) {
  const t = useT();
  const { householdId, requestSync } = useSession();
  const toast = useToast();
  const connections = useBankConnections();
  const accounts = useAccounts(true);
  const reconnect = useReconnect();
  const [busy, setBusy] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<string | null>(null);
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const household = useHousehold();
  if (connections.length === 0) return null;

  async function run(id: string, action: () => Promise<unknown>, done: (result: unknown) => string) {
    setBusy(id);
    try {
      const result = await action();
      requestSync();
      toast(done(result), <Check aria-hidden="true" />);
    } catch (cause) {
      toast(errorText(t, cause), <AlertTriangle aria-hidden="true" />);
    } finally {
      setBusy(null);
      setConfirm(null);
    }
  }

  return (
    <Card title={t.t('banks.title')} flush>
      <div style={{ padding: '4px 0' }}>
        {connections.map((c) => {
          const linked = c.accounts.filter((a) => a.status === 'LINKED').length;
          const pending = c.accounts.some((a) => a.status === 'PENDING');
          const renew = c.reauthorizationRequired || c.status === 'CONSENT_EXPIRED' || c.status === 'EXPIRED' || c.status === 'ACTION_REQUIRED' || c.status === 'CONSENT_EXPIRING';
          // By the clock ("dziś 15:41"): the owner compares the figures with the bank's own app.
          const nextRead = c.nextRefreshAt && Date.parse(c.nextRefreshAt) > Date.now() ? t.t('banks.nextRead', { time: t.when(c.nextRefreshAt) }) : null;
          // Lunch Flow reaches the banks itself: its own words for the state, and the banks behind it.
          const lunchFlow = c.provider === 'LUNCHFLOW';
          const followedEntries = c.accounts.filter((a) => a.status === 'LINKED');
          const banks = [...new Set(followedEntries.map((a) => a.institutionName).filter((n): n is string => Boolean(n)))];
          const stale = !c.lastSuccessAt || Date.now() - Date.parse(c.lastSuccessAt) > DAY_MS;
          const keyRefused = c.lastErrorCode === 'UNAUTHORIZED' || c.lastErrorCode === 'FORBIDDEN';
          const state: { text: string; tone: 'ok' | 'warn' | 'bad' } = !lunchFlow
            ? { text: t.dynamic(`integrations.status.${c.status}`), tone: TONE[c.status] }
            : busy === c.id
              ? { text: t.t('banks.lunchFlow.syncing'), tone: 'ok' }
              : keyRefused
                ? { text: t.dynamic('integrations.status.ACTION_REQUIRED'), tone: 'warn' }
                : c.status === 'CONSENT_EXPIRING' && !c.reauthorizationRequired
                  ? { text: t.t('banks.lunchFlow.expiring'), tone: 'warn' }
                  : renew
                  ? { text: t.t('banks.lunchFlow.reauthorizationRequired'), tone: 'warn' }
                  : c.status === 'ERROR' || c.status === 'RATE_LIMITED'
                    ? { text: t.t('banks.lunchFlow.unavailable'), tone: 'warn' }
                    : stale
                      ? { text: t.t('banks.lunchFlow.stale'), tone: 'warn' }
                      : { text: t.t('banks.lunchFlow.connected'), tone: 'ok' };
          // At a glance: the state, when it last read, how many accounts. The rest on request —
          // opened by itself when an account needs the owner (the owner: "shorter, or details on a click").
          const trouble = lunchFlow && followedEntries.some((a) => a.visible === false || a.access === 'RECONNECT' || a.access === 'ERROR');
          const expanded = trouble !== open.has(c.id);
          const summary = [
            c.lastSuccessAt ? t.t(lunchFlow ? 'banks.lunchFlow.lastSync' : 'integrations.freshness.syncedAgo', { time: t.when(c.lastSuccessAt) }) : null,
            t.plural('banks.accountsCount', linked),
          ].filter(Boolean);
          const detail = [
            lunchFlow ? banks.join(', ') || null : null,
            nextRead,
            c.consentExpiresAt ? t.t(lunchFlow ? 'banks.lunchFlow.renewBy' : 'banks.consentUntil', { date: t.date(c.consentExpiresAt.slice(0, 10), 'dayMonth') }) : null,
          ].filter(Boolean);
          const refresh = () =>
            void run(
              c.id,
              () => bankingApi.refresh(householdId!, c.id),
              (r) => t.t(lunchFlow ? 'banks.lunchFlow.synced' : 'banks.refreshed', { count: String((r as { stats?: { inserted?: number } | null }).stats?.inserted ?? 0) }),
            );
          const lunchFlowLink = (
            <a className="btn btn--ghost btn--sm" href={LUNCHFLOW_URL} target="_blank" rel="noopener noreferrer">
              <ExternalLink aria-hidden="true" />
              {t.t('banks.lunchFlow.openLunchFlow')}
            </a>
          );
          return (
            <div key={c.id} className="list-row bank-connection">
              <span className="account-mark bank-option__mark" aria-hidden="true">
                {mark(c.institutionName)}
              </span>
              <div className="stack bank-connection__info">
                <span className="row gap-2" style={{ minWidth: 0, flexWrap: 'wrap' }}>
                  <strong className="truncate">{c.institutionName}</strong>
                  <span className={`t212-status t212-status--${state.tone}`}>
                    <span className="sync-dot" aria-hidden="true" />
                    {state.text}
                  </span>
                </span>
                <span className="list-row__meta">{summary.join(' · ')}</span>
              </div>
              <div className="row gap-2 bank-connection__actions">
                {pending && (
                  <button className="btn btn--secondary btn--sm" type="button" onClick={() => onChoose(c)}>
                    <ListChecks aria-hidden="true" />
                    {t.t('banks.chooseAccounts')}
                  </button>
                )}
                {lunchFlow ? (
                  <>
                    {/* Our import only: Lunch Flow reads the bank on its own schedule. */}
                    <button className="btn btn--secondary btn--sm" type="button" title={t.t('banks.lunchFlow.syncNowHint')} disabled={busy !== null || !householdId} onClick={refresh}>
                      <RefreshCw aria-hidden="true" className={busy === c.id ? 'spin' : undefined} />
                      {t.t('banks.lunchFlow.syncNow')}
                    </button>
                    {(renew || keyRefused) && lunchFlowLink}
                  </>
                ) : renew ? (
                  <button className="btn btn--secondary btn--sm" type="button" onClick={() => void reconnect(c)}>
                    <Landmark aria-hidden="true" />
                    {t.t('banks.reconnect')}
                  </button>
                ) : (
                  <button className="btn btn--secondary btn--sm" type="button" disabled={busy !== null || !householdId} onClick={refresh}>
                    <RefreshCw aria-hidden="true" className={busy === c.id ? 'spin' : undefined} />
                    {t.t('banks.refresh')}
                  </button>
                )}
                <button
                  className="btn btn--ghost btn--sm"
                  type="button"
                  aria-expanded={expanded}
                  aria-controls={`bank-details-${c.id}`}
                  onClick={() =>
                    setOpen((was) => {
                      const next = new Set(was);
                      if (next.has(c.id)) next.delete(c.id);
                      else next.add(c.id);
                      return next;
                    })
                  }
                >
                  {t.t('banks.details')}
                  <ChevronDown aria-hidden="true" className={`bank-connection__chevron${expanded ? ' is-open' : ''}`} />
                </button>
              </div>
              {expanded && (
                <div className="bank-connection__details" id={`bank-details-${c.id}`}>
                  {detail.length > 0 && <span className="list-row__meta">{detail.join(' · ')}</span>}
                  {c.lastRun && <span className="list-row__meta">{t.t('banks.lastRun', { inserted: String(c.lastRun.inserted), paired: String(c.lastRun.paired) })}</span>}
                  {lunchFlow && followedEntries.length > 0 && (
                    <ul className="bank-connection__accounts">
                      {followedEntries.map((a) => {
                        const problem = a.visible === false ? 'accountHidden' : a.access === 'RECONNECT' ? 'accountReconnect' : a.access === 'ERROR' ? 'accountTrouble' : null;
                        return (
                          <li key={a.externalId}>
                            <span className="truncate">{accounts.find((x) => x.id === a.accountId)?.name ?? a.name}</span>
                            {banks.length > 1 && a.institutionName && <span className="text-muted">{a.institutionName}</span>}
                            {a.currency !== household?.baseCurrency && <span className="text-muted">{a.currency}</span>}
                            <span className={problem ? 'fg-warning' : 'text-muted'}>{t.dynamic(`banks.lunchFlow.${problem ?? 'accountActive'}`)}</span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {lunchFlow && <span className="list-row__meta bank-connection__note">{t.t('banks.lunchFlow.schedule')}</span>}
                  <div className="row gap-2" style={{ flexWrap: 'wrap' }}>
                    {lunchFlow && !(renew || keyRefused) && lunchFlowLink}
                    {confirm === c.id ? (
                      <button className="btn btn--danger btn--sm" type="button" disabled={busy !== null} onClick={() => void run(c.id, () => bankingApi.disconnect(householdId!, c.id), () => t.t('banks.disconnected'))}>
                        <Check aria-hidden="true" />
                        {t.t('banks.disconnect')}?
                      </button>
                    ) : (
                      <button className="btn btn--ghost btn--sm" type="button" disabled={busy !== null} onClick={() => setConfirm(c.id)}>
                        <Unlink aria-hidden="true" />
                        {t.t('banks.disconnect')}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
